import moment, { Moment, MomentInput } from 'moment';
import { RawItemDataSuggestion } from './types';
import { ItemModel } from '../Item/Item';
import { JSONObject } from '../Api/types';
import { ProcessStatus, MeasurementData } from '../types';
import { Cloneable } from '../Utils/Cloneable';
import { AuthenticityInfoData } from '../Item/types';
import { parseCurrencyCode, parsePriceAmount } from 'utils/currency';

type SuggestionRecord = Record<string, unknown>;

export class ItemDataSuggestionModel extends Cloneable {
  id: string;
  itemId: string;
  item: ItemModel;
  data: JSONObject;
  status: ProcessStatus;

  constructor(rawData: RawItemDataSuggestion) {
    super();
    this.id = rawData.id;
    this.itemId = rawData.item_id;
    this.item = new ItemModel(rawData.item);
    this.data = rawData.data;
    this.status = rawData.status;
  }

  public isPending(): boolean {
    return this.status === ProcessStatus.PENDING;
  }

  public isApproved(): boolean {
    return this.status === ProcessStatus.APPROVED;
  }

  public isRejected(): boolean {
    return this.status === ProcessStatus.REJECTED;
  }

  public getUpdatedItem(): ItemModel {
    const updatedItem = ItemModel.deepCopy(this.item);

    const name = this.getSuggestionValue<string>('name');
    if (name !== undefined) {
      updatedItem.name = name;
    }

    const releasePrice = this.getSuggestionValue<string | number>('releasePrice', 'release_price');
    if (releasePrice !== undefined) {
      const priceString = typeof releasePrice === 'number' ? releasePrice.toString() : String(releasePrice);
      const parsedAmount =
        typeof releasePrice === 'number' ? releasePrice : parsePriceAmount(priceString);
      const parsedCurrency =
        parseCurrencyCode(priceString) || updatedItem.releaseInfo.symbol || '';
      updatedItem.releasePrice = priceString;
      updatedItem.releaseInfo = {
        ...updatedItem.releaseInfo,
        rawPrice: priceString,
        symbol: parsedCurrency,
        convertedPrice:
          parsedCurrency?.toUpperCase() === 'USD' && parsedAmount !== undefined
            ? parsedAmount
            : updatedItem.releaseInfo.convertedPrice
      };
    }

    const publisher = this.getSuggestionValue<string>('publisher');
    if (publisher !== undefined) {
      updatedItem.publisher = publisher;
    }

    const edition = this.getSuggestionValue<string | number>('edition');
    if (edition !== undefined) {
      const editionValue = typeof edition === 'number' ? edition.toString() : String(edition);
      updatedItem.edition = editionValue;
      const editionSize = Number(edition);
      updatedItem.editionInfo = {
        ...updatedItem.editionInfo,
        size: Number.isFinite(editionSize) ? editionSize : updatedItem.editionInfo.size
      };
    }

    const dateValue = this.getSuggestionValue<string | Date>('date', 'release_date');
    if (dateValue !== undefined) {
      const parsedDate = this.parseDateValue(dateValue);
      updatedItem.date = parsedDate;
      updatedItem.releaseInfo = {
        ...updatedItem.releaseInfo,
        date: parsedDate
      };
    }

    const dimensions = this.getSuggestionValue<MeasurementData>('dimensions');
    if (dimensions !== undefined) {
      updatedItem.dimensions = dimensions;
    }

    const imageUrl = this.getSuggestionValue<string>('imageUrl', 'image_url');
    if (imageUrl !== undefined) {
      updatedItem.image = imageUrl;
    }

    const material = this.getSuggestionValue<string>('material');
    if (material !== undefined) {
      updatedItem.material = material;
    }

    const materialIds = this.getSuggestionValue<string[] | null>('materialIds', 'material_ids');
    if (materialIds !== undefined) {
      updatedItem.materialIds = Array.isArray(materialIds) ? [...materialIds] : [];
    }

    const artistId = this.getSuggestionValue<string>('artistId', 'artist_id');
    if (artistId !== undefined) {
      updatedItem.artistId = artistId;
      if (!(updatedItem.artist instanceof Object)) {
        updatedItem.artist = artistId;
      }
    }

    const handTreated = this.getSuggestionValue<boolean>('handTreated', 'hand_treated');
    if (handTreated !== undefined) {
      updatedItem.handTreated = handTreated;
    }

    const authenticityInfo = this.getSuggestionValue<JSONObject>('authenticityInfo', 'authenticity_info');
    if (authenticityInfo !== undefined && authenticityInfo !== null && typeof authenticityInfo === 'object') {
      updatedItem.authenticityInfo = this.mergeAuthenticityInfo(
        updatedItem.authenticityInfo,
        authenticityInfo as SuggestionRecord
      );
    }

    return updatedItem;
  }

  private getSuggestionValue<T>(...keys: string[]): T | undefined {
    const suggestionRecord = this.data as SuggestionRecord;
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(suggestionRecord, key)) {
        const value = suggestionRecord[key];
        if (value !== undefined) {
          return value as T;
        }
      }
    }
    return undefined;
  }

  private parseDateValue(value: string | Date): Moment | null {
    if (!value) {
      return null;
    }

    const dateValue: MomentInput = value instanceof Date ? value : String(value);
    const parsed = moment(dateValue);
    return parsed.isValid() ? parsed : null;
  }

  private mergeAuthenticityInfo(
    original: AuthenticityInfoData,
    suggestion: SuggestionRecord
  ): AuthenticityInfoData {
    const updated = { ...original };

    const certificateIssued = this.resolveBoolean(suggestion, 'certificateIssued', 'certificate_issued');
    if (certificateIssued !== undefined) {
      updated.certificateIssued = certificateIssued;
    }

    const dated = this.resolveBoolean(suggestion, 'dated');
    if (dated !== undefined) {
      updated.dated = dated;
    }

    const hasFakes = this.resolveBoolean(suggestion, 'hasFakes', 'has_fakes');
    if (hasFakes !== undefined) {
      updated.hasFakes = hasFakes;
    }

    const numbered = this.resolveBoolean(suggestion, 'numbered');
    if (numbered !== undefined) {
      updated.numbered = numbered;
    }

    const signed = this.resolveBoolean(suggestion, 'signed');
    if (signed !== undefined) {
      updated.signed = signed;
    }

    return updated;
  }

  private resolveBoolean(source: SuggestionRecord, ...keys: string[]): boolean | undefined {
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        const value = source[key];
        if (value !== undefined && value !== null) {
          return Boolean(value);
        }
      }
    }
    return undefined;
  }
}
